import { useState, useEffect } from 'react';
import { Wind, Eye, Hand, Ear, Flower2, Coffee, BookHeart, Play, Pause, Check, RotateCcw, Sparkles, Heart } from 'lucide-react';
import { BREATHING, GROUNDING_54321, JOURNAL_PROMPTS } from '../lib/constants';
import { addBreathingSession, addJournalEntry, getJournalEntries } from '../lib/mock';

const SENSE_ICONS = { Eye, Hand, Ear, Flower2, Coffee };

export default function ToolsScreen({ patient }) {
  const [tool, setTool] = useState(null); // 'breathing', 'grounding', 'journal'
  const [selBreath, setSelBreath] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);

  useEffect(() => { (async () => setJournalEntries(await getJournalEntries(patient.id)))(); }, [tool]);

  if (tool === 'breathing' && selBreath) return <BreathingExercise breath={selBreath} onDone={() => { setSelBreath(null); addBreathingSession(patient.id, { id: Date.now(), created_at: new Date().toISOString(), type: selBreath.id }); }} onBack={() => setSelBreath(null)} />;
  if (tool === 'grounding') return <GroundingExercise onBack={() => setTool(null)} />;
  if (tool === 'journal') return <JournalTool patient={patient} entries={journalEntries} onBack={() => setTool(null)} onSaved={async () => setJournalEntries(await getJournalEntries(patient.id))} />;

  return (
    <div className="px-4 pt-4 pb-6 max-w-2xl mx-auto">
      <div className="mb-4">
        <h2 className="text-xl font-extrabold" style={{ color: 'var(--text)' }}>أدواتي</h2>
        <p className="text-[12.5px]" style={{ color: 'var(--text-2)' }}>تقنيات لتحسين جودة حياتك وتهدئة لحظاتك الصعبة</p>
      </div>

      {/* Breathing */}
      <div className="card-soft p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'var(--primary-bg)' }}>
            <Wind className="w-4 h-4" style={{ color: 'var(--primary-dark)' }} />
          </div>
          <div className="flex-1">
            <div className="text-[13.5px] font-extrabold" style={{ color: 'var(--text)' }}>تمارين التنفس</div>
            <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>هدوء فوري واسترخاء عميق</div>
          </div>
        </div>
        <div className="grid gap-2">
          {BREATHING.map(b => (
            <button key={b.id} onClick={() => { setSelBreath(b); setTool('breathing'); }} className="text-right rounded-2xl p-3 border transition-all hover:shadow-md active:scale-[0.99]" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-soft)' }}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${b.color}22`, border: `2px solid ${b.color}55` }}>
                  <Play className="w-4 h-4" style={{ color: b.color }} fill={b.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-extrabold" style={{ color: 'var(--text)' }}>{b.name}</div>
                  <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>{b.subtitle} • {b.when}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Grounding */}
      <button onClick={() => setTool('grounding')} className="w-full card-soft p-4 mb-4 text-right hover:shadow-md active:scale-[0.99] transition-all">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--secondary-bg)' }}>
            <Sparkles className="w-4.5 h-4.5" style={{ color: 'var(--secondary-dark)' }} />
          </div>
          <div className="flex-1">
            <div className="text-[13.5px] font-extrabold" style={{ color: 'var(--text)' }}>تقنية التأريض 5-4-3-2-1</div>
            <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>لحظات القلق والفزع — ارجعي للحاضر</div>
          </div>
        </div>
      </button>

      {/* Journal */}
      <button onClick={() => setTool('journal')} className="w-full card-soft p-4 mb-4 text-right hover:shadow-md active:scale-[0.99] transition-all">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--sage-bg)' }}>
            <BookHeart className="w-4.5 h-4.5" style={{ color: '#5a8656' }} />
          </div>
          <div className="flex-1">
            <div className="text-[13.5px] font-extrabold" style={{ color: 'var(--text)' }}>يوميّاتي الدافئة</div>
            <div className="text-[10.5px]" style={{ color: 'var(--text-2)' }}>{journalEntries.length} كتابة • اكتبي ما يدور في داخلك</div>
          </div>
        </div>
      </button>

      {/* Tips */}
      <div className="rounded-2xl p-4 border" style={{ background: 'linear-gradient(135deg, var(--honey-bg), var(--peach-bg))', borderColor: 'var(--honey)' }}>
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-4 h-4" style={{ color: '#c9871f' }} fill="#c9871f" />
          <span className="text-[11px] font-extrabold" style={{ color: '#8a5f1a' }}>تذكير لطيف</span>
        </div>
        <p className="text-[12px] leading-relaxed" style={{ color: '#6b4e14' }}>
          تمارين التنفس والتأريض تعمل أفضل عند الممارسة اليومية — حتى دقيقتان تصنع فرقاً.
        </p>
      </div>
    </div>
  );
}

// ============ Breathing Exercise ============
function BreathingExercise({ breath, onDone, onBack }) {
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(0);
  const [count, setCount] = useState(breath.pattern[0].duration);
  const [cycle, setCycle] = useState(1);

  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => {
      setCount(c => {
        if (c > 1) return c - 1;
        setPhase(p => {
          const next = p + 1;
          if (next >= breath.pattern.length) {
            if (cycle >= breath.cycles) { setRunning(false); setTimeout(onDone, 500); return p; }
            setCycle(c => c + 1);
            setCount(breath.pattern[0].duration);
            return 0;
          }
          setCount(breath.pattern[next].duration);
          return next;
        });
        return breath.pattern[Math.min(phase + 1, breath.pattern.length - 1)].duration;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [running, phase, cycle]);

  const current = breath.pattern[phase];
  const scale = current.phase === 'inhale' ? 1.4 : current.phase === 'exhale' ? 0.7 : 1.05;

  return (
    <div className="px-4 pt-4 pb-6 max-w-2xl mx-auto flex flex-col items-center min-h-[calc(100vh-140px)]">
      <div className="w-full flex items-center mb-6">
        <button onClick={onBack} className="text-[13px] font-semibold flex items-center gap-1" style={{ color: 'var(--text-2)' }}>
          <RotateCcw className="w-3.5 h-3.5" /> رجوع
        </button>
        <div className="flex-1"></div>
        <div className="text-[11px]" style={{ color: 'var(--text-2)' }}>{running ? `دورة ${cycle}/${breath.cycles}` : ''}</div>
      </div>

      <div className="text-center mb-6">
        <h2 className="text-2xl font-extrabold mb-1" style={{ color: 'var(--text)' }}>{breath.name}</h2>
        <p className="text-[12.5px]" style={{ color: 'var(--text-2)' }}>{breath.subtitle}</p>
      </div>

      {/* Breathing circle */}
      <div className="relative flex-1 flex items-center justify-center w-full mb-6">
        <div className="absolute rounded-full transition-all duration-1000"
             style={{ width: 240, height: 240, background: `radial-gradient(circle, ${breath.color}30, ${breath.color}05)`, transform: `scale(${running ? scale : 1})`, transitionDuration: `${running ? current?.duration || 1 : 1}s` }}></div>
        <div className="absolute rounded-full transition-all"
             style={{ width: 180, height: 180, background: `radial-gradient(circle, ${breath.color}50, ${breath.color}20)`, transform: `scale(${running ? scale : 1})`, transitionDuration: `${running ? current?.duration || 1 : 1}s` }}></div>
        <div className="relative rounded-full flex flex-col items-center justify-center shadow-2xl transition-all"
             style={{ width: 140, height: 140, background: `linear-gradient(135deg, ${breath.color}, ${breath.color}dd)`, transform: `scale(${running ? scale : 1})`, transitionDuration: `${running ? current?.duration || 1 : 1}s` }}>
          <div className="text-white text-[13px] font-bold mb-1">{running ? current.label : 'ابدأي'}</div>
          <div className="text-white text-4xl font-extrabold">{running ? count : breath.cycles}</div>
          {!running && <div className="text-white text-[10px] mt-1 opacity-80">دورة</div>}
        </div>
      </div>

      <button
        onClick={() => { if (!running) { setRunning(true); setPhase(0); setCycle(1); setCount(breath.pattern[0].duration); } else { setRunning(false); } }}
        className="w-full py-4 rounded-2xl text-white font-extrabold text-[15px] flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-transform"
        style={{ background: `linear-gradient(135deg, ${breath.color}, ${breath.color}dd)` }}
      >
        {running ? <><Pause className="w-4 h-4" /> توقف</> : <><Play className="w-4 h-4" /> ابدأي التمرين</>}
      </button>

      <div className="text-[11.5px] text-center mt-3 leading-relaxed" style={{ color: 'var(--text-2)' }}>
        {breath.description}
      </div>
    </div>
  );
}

// ============ Grounding Exercise ============
function GroundingExercise({ onBack }) {
  const [step, setStep] = useState(0);
  const [inputs, setInputs] = useState({});
  const cur = GROUNDING_54321[step];
  const Icon = SENSE_ICONS[cur?.icon] || Eye;
  const [items, setItems] = useState(Array(cur?.count || 0).fill(''));

  useEffect(() => { setItems(Array(cur?.count || 0).fill('')); }, [step]);

  const next = () => {
    if (step >= GROUNDING_54321.length - 1) { onBack(); return; }
    setInputs({ ...inputs, [cur.sense]: items });
    setStep(step + 1);
  };

  return (
    <div className="px-4 pt-4 pb-6 max-w-2xl mx-auto">
      <div className="flex items-center mb-4">
        <button onClick={onBack} className="text-[13px] font-semibold flex items-center gap-1" style={{ color: 'var(--text-2)' }}>
          <RotateCcw className="w-3.5 h-3.5" /> رجوع
        </button>
        <div className="flex-1 text-center">
          <div className="text-[11px]" style={{ color: 'var(--text-2)' }}>{step + 1} / {GROUNDING_54321.length}</div>
        </div>
        <div className="w-16"></div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full mb-6 overflow-hidden" style={{ background: 'var(--border-soft)' }}>
        <div className="h-full transition-all duration-500" style={{ width: `${((step + 1) / GROUNDING_54321.length) * 100}%`, background: `linear-gradient(90deg, ${cur.color}, ${cur.color}bb)` }}></div>
      </div>

      <div className="text-center mb-6">
        <div className="w-20 h-20 rounded-3xl mx-auto flex items-center justify-center mb-3" style={{ background: `${cur.color}18`, border: `2px solid ${cur.color}44` }}>
          <Icon className="w-9 h-9" style={{ color: cur.color }} />
        </div>
        <div className="text-6xl font-extrabold mb-2" style={{ color: cur.color }}>{cur.count}</div>
        <h2 className="text-2xl font-extrabold mb-1" style={{ color: 'var(--text)' }}>أشياء {cur.sense}</h2>
        <p className="text-[13px] leading-relaxed max-w-[300px] mx-auto" style={{ color: 'var(--text-2)' }}>{cur.hint}</p>
      </div>

      <div className="space-y-2 mb-6">
        {items.map((v, i) => (
          <input
            key={i}
            value={v}
            onChange={e => { const n = [...items]; n[i] = e.target.value; setItems(n); }}
            placeholder={`${i + 1}. اكتبي هنا...`}
            className="w-full bg-[var(--surface)] border-2 border-[var(--border-soft)] focus:border-[var(--primary)] rounded-2xl px-4 py-3 text-[var(--text)] outline-none transition-colors"
            style={{ '--focus': cur.color }}
          />
        ))}
      </div>

      <button onClick={next} className="w-full py-3.5 rounded-2xl text-white font-extrabold text-[14px] shadow-md active:scale-[0.98] transition-transform" style={{ background: `linear-gradient(135deg, ${cur.color}, ${cur.color}cc)` }}>
        {step >= GROUNDING_54321.length - 1 ? 'انتهيت ✓' : 'التالي ←'}
      </button>
    </div>
  );
}

// ============ Journal Tool ============
function JournalTool({ patient, entries, onBack, onSaved }) {
  const [selPrompt, setSelPrompt] = useState(JOURNAL_PROMPTS[0]);
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(false);

  const save = async () => {
    if (!text.trim()) return;
    await addJournalEntry(patient.id, { id: Date.now(), created_at: new Date().toISOString(), prompt: selPrompt, text: text.trim() });
    setText('');
    setSaved(true);
    onSaved();
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="px-4 pt-4 pb-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} className="text-[13px] font-semibold flex items-center gap-1" style={{ color: 'var(--text-2)' }}>
          <RotateCcw className="w-3.5 h-3.5" /> رجوع
        </button>
        <div className="text-[12px] font-bold" style={{ color: 'var(--text)' }}>يوميّاتي</div>
        <div className="w-12"></div>
      </div>

      <div className="card-soft p-4 mb-3">
        <div className="text-[10px] font-bold mb-2" style={{ color: 'var(--text-2)' }}>اختاري سؤالاً:</div>
        <div className="flex flex-col gap-1.5 mb-3">
          {JOURNAL_PROMPTS.map((p, i) => (
            <button key={i} onClick={() => setSelPrompt(p)} className={`text-right rounded-xl px-3 py-2 text-[12px] font-semibold transition-all border ${selPrompt === p ? 'shadow-sm' : ''}`} style={selPrompt === p ? { background: 'var(--sage-bg)', borderColor: '#a3c9a1', color: '#4a6f4a' } : { background: 'var(--surface-2)', borderColor: 'var(--border-soft)', color: 'var(--text-2)' }}>
              {p}
            </button>
          ))}
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={5} placeholder="ابدئي الكتابة هنا بحرية…"
          className="w-full bg-[var(--surface-2)] border-2 border-[var(--border-soft)] focus:border-[#a3c9a1] rounded-2xl p-3 text-[var(--text)] text-[14px] resize-none outline-none leading-relaxed" />
        <button onClick={save} disabled={!text.trim()} className="w-full mt-3 py-3 rounded-2xl text-white font-extrabold text-[13.5px] shadow-md active:scale-[0.98] transition-transform disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #7dcfa9, #5aa87f)' }}>
          {saved ? '✓ محفوظ' : 'حفظ اليومية'}
        </button>
      </div>

      {entries.length > 0 && (
        <>
          <div className="text-[10px] font-extrabold uppercase tracking-wider mt-4 mb-2" style={{ color: 'var(--text-2)' }}>يومياتي السابقة</div>
          <div className="space-y-2">
            {entries.slice(0, 10).map(e => {
              const d = new Date(e.created_at);
              return (
                <div key={e.id} className="card-soft p-3">
                  <div className="text-[10px] mb-1.5 flex items-center justify-between" style={{ color: 'var(--text-2)' }}>
                    <span className="font-semibold">{d.toLocaleDateString('ar-SA')}</span>
                    <span>{d.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div className="text-[11px] font-bold mb-1" style={{ color: 'var(--secondary-dark)' }}>{e.prompt}</div>
                  <div className="text-[12.5px] whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--text)' }}>{e.text}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
